import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PpobService from 'service/PPOB/PpobService';
import {
  ProductItem,
  ProductItemConfigResponse,
} from 'domain/payload/response/PPOB/PpobProductItemResp';
import { hasPpobAccess } from 'helpers/hasPrivilege';
import CustomDropdown, { DropdownOption } from 'component/CustomDropdown';
import { PageSizeOption } from 'domain/constant/PageSizeOption';
import PpobProductItemPricingModal from './PpobProductItemPricingModal';
import PpobProductItemDetailModal from './PpobProductItemDetailModal';

const PpobProductItem: React.FC = () => {
  const { profile } = useSelector((state: any) => state.auth);
  const ppobService = PpobService();

  const [config, setConfig] = useState<ProductItemConfigResponse | null>(null);
  const [data, setData] = useState<ProductItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [selectedProductId, setSelectedProductId] = useState<string>('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Toggling state
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Modals
  const [pricingModalItem, setPricingModalItem] = useState<ProductItem | null>(null);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);

  const [detailModalItemId, setDetailModalItemId] = useState<string | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const canUpdate = hasPpobAccess(profile, 'update');

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      maximumFractionDigits: 0,
    }).format(val || 0);
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <span className="badge bg-success">Active</span>
    ) : (
      <span className="badge bg-danger">Inactive</span>
    );
  };

  // 1. Fetch dropdown configuration
  const fetchConfig = async () => {
    const token = profile?.data?.token;
    if (!token) return;

    try {
      const res = await ppobService.getProductItemConfig(token);
      if (res?.data) {
        setConfig(res.data);
      }
    } catch (err: any) {
      console.error('Failed to fetch PPOB item config:', err);
    }
  };

  // 2. Fetch product item list
  const fetchData = async () => {
    const token = profile?.data?.token;
    if (!token) {
      window.alert('Token not found. Please log in again.');
      return;
    }

    setLoading(true);
    try {
      const res = await ppobService.getProductItemList(token, {
        page: currentPage,
        size: pageSize,
        search: searchKeyword || undefined,
        groupId: selectedGroupId || undefined,
        productId: selectedProductId || undefined,
      });

      setData(res.data || []);
      setTotalPages(res.pagination?.totalPage ?? 1);
      setTotalRecords(res.pagination?.totalRecords ?? 0);
    } catch (err: any) {
      console.error('Failed to fetch PPOB product items:', err);
      const msg = typeof err === 'string' ? err : err.message || 'Failed to fetch product items';
      window.alert(`Error: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, searchKeyword, selectedGroupId, selectedProductId]);

  // Options for Group dropdown
  const groupOptions: DropdownOption[] = useMemo(() => {
    if (!config?.groupList) return [];
    return config.groupList.map((g) => ({
      id: g.id,
      name: g.name,
    }));
  }, [config]);

  // Options for Product dropdown (cascading based on selectedGroupId)
  const productOptions: DropdownOption[] = useMemo(() => {
    if (!config?.productList) return [];
    const filtered = selectedGroupId
      ? config.productList.filter((p) => p.groupId === selectedGroupId)
      : config.productList;

    return filtered.map((p) => ({
      id: p.id,
      name: p.name,
    }));
  }, [config, selectedGroupId]);

  const handleGroupChange = (option: DropdownOption) => {
    const newGroupId = option.id ? String(option.id) : '';
    setSelectedGroupId(newGroupId);
    setSelectedProductId('');
    setCurrentPage(1);
  };

  const handleProductChange = (option: DropdownOption) => {
    setSelectedProductId(option.id ? String(option.id) : '');
    setCurrentPage(1);
  };

  const handleSearchClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSearchKeyword(searchTerm.trim());
    setCurrentPage(1);
  };

  const handleToggleActive = async (item: ProductItem) => {
    if (!canUpdate) return;
    const token = profile?.data?.token;
    if (!token) return;

    const newActiveState = !item.isActive;

    // Optimistic UI update
    setData((prev) =>
      prev.map((i) => (i.id === item.id ? { ...i, isActive: newActiveState } : i))
    );

    setTogglingId(item.id);
    try {
      await ppobService.toggleProductItemActive(token, {
        id: item.id,
        isActive: newActiveState,
      });
    } catch (err: any) {
      console.error('Failed to toggle item active:', err);
      window.alert('Failed to update product item status. Please try again.');
      // Rollback
      setData((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, isActive: item.isActive } : i))
      );
    } finally {
      setTogglingId(null);
    }
  };

  const handleExportToXlsx = async () => {
    const token = profile?.data?.token;
    if (!token) {
      window.alert('Token not found. Please log in again.');
      return;
    }

    setExporting(true);
    try {
      await ppobService.exportProductItems(token, {
        format: 'xlsx',
        groupId: selectedGroupId || undefined,
        productId: selectedProductId || undefined,
        search: searchKeyword || undefined,
      });
    } catch (err: any) {
      console.error('Failed to export:', err);
      const msg = typeof err === 'string' ? err : err.message || 'Failed to export product items';
      window.alert(`Error: ${msg}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="d-flex">
            <h5 style={{ flex: 1 }}>PPOB Items</h5>
            <div>
              <p>TransMore PPOB Items</p>
            </div>
          </div>
        </div>
        <div className="col-12">
          <div className="p-4" style={{ background: 'white' }}>
            <div className="mb-3 d-flex flex-nowrap gap-2 overflow-auto text-nowrap">
              <form className="d-flex" onSubmit={handleSearchClick}>
                <input
                  type="text"
                  className="form-control form-control-compact me-2"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ minWidth: 200 }}
                />
                <button type="submit" className="px-4 btn btn-primary">
                  <i className="fa-solid fa-search"></i>
                </button>
              </form>

              <div>
                <CustomDropdown
                  value={selectedGroupId || null}
                  options={groupOptions}
                  showDefaultOption={true}
                  defaultOptionLabel="All Group"
                  searchable={true}
                  searchPlaceholder="Search Group..."
                  onChange={handleGroupChange}
                  buttonStyle={{ width: 180 }}
                  dropdownStyle={{ minWidth: 200 }}
                />
              </div>

              <div>
                <CustomDropdown
                  value={selectedProductId || null}
                  options={productOptions}
                  showDefaultOption={true}
                  defaultOptionLabel="All Product"
                  searchable={true}
                  searchPlaceholder="Search Product..."
                  onChange={handleProductChange}
                  buttonStyle={{ width: 180 }}
                  dropdownStyle={{ minWidth: 200 }}
                />
              </div>

              <div className="d-flex justify-content-end" style={{ flex: 1 }}>
                <button
                  className="btn btn-outline-primary d-flex justify-content-center align-items-center"
                  onClick={handleExportToXlsx}
                  disabled={exporting}
                >
                  {exporting && (
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  )}
                  Export To Excel
                </button>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-condensed table-hover">
                <thead className="table-primary">
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Group</th>
                    <th>Product</th>
                    <th>Biller</th>
                    <th>Price</th>
                    <th>Transmora Margin</th>
                    <th>Partner Margin</th>
                    <th>Discount</th>
                    <th>Estimated Price</th>
                    <th>Product Status</th>
                    <th>Item Status</th>
                    <th style={{ width: '100px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {!loading ? (
                    data.length > 0 ? (
                      data.map((item) => {
                        const transmoraMargin = item.pricing?.transmoraMargin ?? item.transmoraMargin ?? 0;
                        const partnerMargin = item.pricing?.defaultPartnerMargin ?? item.defaultPartnerMargin ?? 0;
                        const discount = item.pricing?.discount ?? item.discount ?? 0;
                        const basePrice = item.price ?? 0;
                        const sellingPrice = Math.max(0, basePrice + transmoraMargin + partnerMargin - discount);

                        return (
                          <tr key={item.id}>
                            <td>{item.code}</td>
                            <td>{item.name}</td>
                            <td>{item.groupName}</td>
                            <td>{item.productName}</td>
                            <td>{item.billerName || '-'}</td>
                            <td>{formatCurrency(basePrice)}</td>
                            <td>{formatCurrency(transmoraMargin)}</td>
                            <td>{formatCurrency(partnerMargin)}</td>
                            <td>{formatCurrency(discount)}</td>
                            <td>{formatCurrency(sellingPrice)}</td>
                            <td>
                              <div
                                className="form-check form-switch d-inline-block"
                                title="You can change this product status in PPOB Configuration page"
                                style={{ cursor: 'not-allowed' }}
                              >
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  checked={item.productIsActive ?? false}
                                  disabled
                                  style={{ transform: 'scale(1.1)', cursor: 'not-allowed', pointerEvents: 'none' }}
                                />
                              </div>
                            </td>
                            <td>
                              <div className="form-check form-switch d-inline-block">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  id={`itemActiveSwitch-${item.id}`}
                                  checked={item.isActive}
                                  onChange={() => handleToggleActive(item)}
                                  disabled={togglingId === item.id || !canUpdate}
                                  style={{ transform: 'scale(1.1)', cursor: canUpdate ? 'pointer' : 'default' }}
                                  title={item.isActive ? 'Item is Active' : 'Item is Inactive'}
                                />
                              </div>
                            </td>
                            <td>
                              <div className="d-flex">
                                <button
                                  className="border-0 btn btn-outline-info btn-small-compact"
                                  onClick={() => {
                                    setDetailModalItemId(item.id);
                                    setIsDetailModalOpen(true);
                                  }}
                                  title="Detail"
                                >
                                  <i className="fa-solid fa-eye"></i>
                                </button>
                                {canUpdate && (
                                  <button
                                    className="border-0 btn btn-outline-warning btn-small-compact ms-2"
                                    onClick={() => {
                                      setPricingModalItem(item);
                                      setIsPricingModalOpen(true);
                                    }}
                                    title="Edit Pricing"
                                  >
                                    <i className="fa-solid fa-pencil"></i>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={13} className="text-center">
                          No data
                        </td>
                      </tr>
                    )
                  ) : (
                    <tr>
                      <td colSpan={13} className="text-center">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-3 d-flex justify-content-end align-items-center">
              <div className="d-flex align-items-center">
                <div className="me-4 d-flex align-items-center">
                  <label className="me-2">Rows per page:</label>
                  <CustomDropdown
                    value={pageSize}
                    options={PageSizeOption.options}
                    onChange={(opt) => {
                      setPageSize(Number(opt.id));
                      setCurrentPage(1);
                    }}
                    align="top"
                  />
                </div>
                <span className="me-3">
                  {totalRecords > 0
                    ? `${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords}`
                    : "0 data"}
                </span>

                <nav>
                  <ul className="mb-0 pagination">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-left"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-left"></i>
                      </button>
                    </li>

                    <li className="page-item disabled">
                      <span className="page-link" style={{ border: 0 }}>{currentPage}</span>
                    </li>

                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-right"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(totalPages)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Edit Modal */}
      <PpobProductItemPricingModal
        isOpen={isPricingModalOpen}
        item={pricingModalItem}
        onClose={() => {
          setIsPricingModalOpen(false);
          setPricingModalItem(null);
        }}
        onSuccess={() => {
          fetchData();
        }}
      />

      {/* Detail Modal */}
      <PpobProductItemDetailModal
        isOpen={isDetailModalOpen}
        itemId={detailModalItemId}
        onClose={() => {
          setIsDetailModalOpen(false);
          setDetailModalItemId(null);
        }}
        onOpenEditPricing={(item) => {
          setPricingModalItem(item);
          setIsPricingModalOpen(true);
        }}
        canUpdate={canUpdate}
      />
    </div>
  );
};

export default PpobProductItem;
